import { memo, type Ref } from "react";
import classNames from "classnames";
import type { Task } from "@common/types/task";
import type { User } from "@common/types/user";
import { TaskColumn } from "@common/types/views/tasksView";
import { BoardCard } from "@design-system/Board/BoardCard";
import { BoardCardLayout } from "@design-system/Board/BoardCardLayout";
import { TaskViewerUsers } from "@components/presence/TaskViewerUsers";
import { AgentRunActivityDrawer } from "@components/presence/AgentRunActivityDrawer";
import { useAgentRunForTask } from "@components/presence/useAgentRunForTask";
import type { TaskBoardTask } from "../types";
import {
    getTaskCardDisplayFields,
    ScopedToTaskWorkspace,
} from "./TaskBoardCard";
import { useTaskCardLabel } from "./taskCardContext";
import { getTaskPresenceField } from "./TaskCardPresence";
import { withStatusField } from "./taskBoardFields";
import { TaskStatusPill } from "./TaskStatusPill";
import cardStyles from "./TaskBoardCard.module.scss";
import styles from "./TaskBoardColumnPanel.module.scss";

interface TaskBoardCompactCardProps {
    task: TaskBoardTask;
    /** The `TaskColumn` ids the full card shows, in order. */
    cardFields: readonly string[];
    /** The `TaskColumn` ids the columns and lanes already show. */
    hiddenFields: ReadonlySet<string>;
    isCrossWorkspace: boolean;
    /** The task open in the detail beside the panel. */
    isOpen: boolean;
    onOpen: (task: Task) => void;
    /** Opens the task with its Dependencies, from the blocked tag. */
    onOpenDependencies: (task: Task) => void;
    ref?: Ref<HTMLDivElement>;
}

/** The compact card inside its task's workspace scope, which its name and fields read. */
const TaskBoardCompactCardContent = ({
    task,
    cardFields,
    hiddenFields,
    isCrossWorkspace,
    isOpen,
    onOpen,
    onOpenDependencies,
    ref,
}: Readonly<TaskBoardCompactCardProps>) => {
    const { run, isWorking: isAgentWorking } = useAgentRunForTask(task.id);
    const fieldArgs = { task, cardFields, hiddenFields, isCrossWorkspace };
    const label = useTaskCardLabel(fieldArgs);
    const renderLayout = (viewers: readonly User[]) => (
        <BoardCardLayout
            title={task.title}
            fields={[
                ...withStatusField({
                    fields: getTaskCardDisplayFields({
                        ...fieldArgs,
                        onOpenDependencies,
                    }),
                    status: !hiddenFields.has(TaskColumn.STATUS) && (
                        <TaskStatusPill
                            status={task.status}
                            className={cardStyles.statusPill}
                        />
                    ),
                }),
                getTaskPresenceField({
                    viewers,
                    assignee: task.assignee ?? null,
                    isAgentWorking,
                }),
            ]}
        />
    );

    return (
        <BoardCard
            ref={ref}
            aria-label={label}
            className={classNames({ [styles.openCard]: isOpen })}
            onClick={() => onOpen(task)}
            drawer={run ? <AgentRunActivityDrawer run={run} /> : undefined}
        >
            {isOpen ? (
                renderLayout([])
            ) : (
                <TaskViewerUsers
                    taskId={task.id}
                    workspaceId={task.workspaceId}
                >
                    {renderLayout}
                </TaskViewerUsers>
            )}
        </BoardCard>
    );
};

/**
 * A task in the open task's column panel: the full card's title, fields,
 * status and presence stack, placed as there but read only (the open task's
 * stack leaves out its viewers). Clicking it opens the task, or closes it
 * when it's open; its blocked tag opens the task's Dependencies and keeps it
 * open.
 */
export const TaskBoardCompactCard = memo(
    (props: Readonly<TaskBoardCompactCardProps>) => (
        <ScopedToTaskWorkspace
            task={props.task}
            isCrossWorkspace={props.isCrossWorkspace}
        >
            <TaskBoardCompactCardContent {...props} />
        </ScopedToTaskWorkspace>
    ),
);

TaskBoardCompactCard.displayName = "TaskBoardCompactCard";
